/**
 * Currency formatting utilities for Ledger app
 * KRW (Korean Won) formatting with thousand separators
 * Negative for expenses, positive for income
 */

/**
 * Format number as KRW with thousand separators
 * @param amount - Amount in KRW (negative for expense, positive for income)
 * @param options - Formatting options
 * @returns Formatted string with thousand separators
 * @example formatKRW(-15000) // "-15,000"
 * @example formatKRW(50000) // "50,000"
 * @example formatKRW(-15000, { showSign: true }) // "-15,000원"
 */
export function formatKRW(
  amount: number,
  options: {
    showSign?: boolean
    showCurrency?: boolean
    absoluteValue?: boolean
  } = {}
): string {
  const { showSign = false, showCurrency = false, absoluteValue = false } = options

  // Handle invalid input
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '0'
  }

  const value = absoluteValue ? Math.abs(amount) : amount
  const isNegative = value < 0

  // Format with thousand separators
  const formatted = Math.abs(value).toLocaleString('ko-KR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  // Build result string
  let result = formatted

  // Add sign
  if (showSign || isNegative) {
    result = (isNegative ? '-' : '+') + result
  }

  // Add currency symbol
  if (showCurrency) {
    result += '원'
  }

  return result
}

/**
 * Parse Korean currency string to number
 * Removes thousand separators and currency symbols
 * @param krwString - KRW string with optional separators
 * @returns Parsed number or null if invalid
 * @example parseKRW("-15,000원") // -15000
 * @example parseKRW("50000") // 50000
 */
export function parseKRW(krwString: string): number | null {
  if (!krwString) return null

  try {
    // Remove currency symbols, spaces, and commas
    const cleaned = krwString.replace(/[원,\s]/g, '').trim()

    // Parse as number
    const value = parseFloat(cleaned)

    if (isNaN(value)) return null

    return value
  } catch {
    return null
  }
}

/**
 * Format amount with direction label
 * @param amount - Amount in KRW
 * @param direction - Transaction direction
 * @returns Formatted string with direction
 * @example formatWithDirection(-15000, "지출") // "지출 15,000원"
 * @example formatWithDirection(50000, "수입") // "수입 50,000원"
 */
export function formatWithDirection(
  amount: number,
  direction: '수입' | '지출'
): string {
  const formatted = formatKRW(Math.abs(amount), { showCurrency: true })
  return `${direction} ${formatted}`
}

/**
 * Format amount as expense/income with color coding
 * @param amount - Amount in KRW (negative for expense, positive for income)
 * @returns Object with formatted string and CSS class
 */
export function formatWithColor(amount: number): {
  text: string
  className: string
} {
  const isExpense = amount < 0
  const formatted = formatKRW(amount, { showSign: true, showCurrency: true })

  return {
    text: formatted,
    className: isExpense ? 'text-red-600' : 'text-blue-600',
  }
}

/**
 * Validate if string is a valid KRW amount
 * @param krwString - KRW string to validate
 * @returns true if valid
 */
export function isValidKRW(krwString: string): boolean {
  if (!krwString) return false

  const parsed = parseKRW(krwString)
  return parsed !== null && !isNaN(parsed)
}

/**
 * Format large numbers with K/M/B suffixes
 * @param amount - Amount in KRW
 * @returns Formatted string with suffix
 * @example formatCompact(1500000) // "1.5M"
 * @example formatCompact(500000) // "500K"
 */
export function formatCompact(amount: number): string {
  const abs = Math.abs(amount)
  const sign = amount < 0 ? '-' : ''

  if (abs >= 1_000_000_000) {
    return sign + (abs / 1_000_000_000).toFixed(1) + 'B'
  } else if (abs >= 1_000_000) {
    return sign + (abs / 1_000_000).toFixed(1) + 'M'
  } else if (abs >= 1_000) {
    return sign + (abs / 1_000).toFixed(0) + 'K'
  }

  return formatKRW(amount)
}

/**
 * Calculate percentage change
 * @param current - Current amount
 * @param previous - Previous amount
 * @returns Formatted percentage with sign
 * @example calculatePercentageChange(150000, 100000) // "+50.0%"
 */
export function calculatePercentageChange(
  current: number,
  previous: number
): string {
  if (previous === 0) return 'N/A'

  const change = ((current - previous) / Math.abs(previous)) * 100
  const sign = change >= 0 ? '+' : ''

  return `${sign}${change.toFixed(1)}%`
}
